import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter'
import HomeIcon from '@mui/icons-material/Home'
import SchoolIcon from '@mui/icons-material/School'
import StarIcon from '@mui/icons-material/Star'
import WorkIcon from '@mui/icons-material/Work'
import { Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, List, ListItem, Stack, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import AutomaticSheet from 'src/components/sheet/AutomaticSheet'
import SheetHeader from 'src/components/sheet/SheetHeader'
import { POI, deletePoi, loadPois } from 'src/lib/pois'

export const Route = createFileRoute('/settings/pois')({
    component: PoisSettings,
})

const ICON_MAP: Record<string, React.ReactNode> = {
    home: <HomeIcon sx={{ color: 'primary.main' }} />,
    work: <WorkIcon sx={{ color: 'primary.main' }} />,
    school: <SchoolIcon sx={{ color: 'primary.main' }} />,
    workout: <FitnessCenterIcon sx={{ color: 'primary.main' }} />,
    star: <StarIcon sx={{ color: 'primary.main' }} />,
}

function PoisSettings() {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [pois, setPois] = useState<POI[]>(loadPois)
    const [deleteTarget, setDeleteTarget] = useState<POI | null>(null)

    const handleDelete = () => {
        if (!deleteTarget) return
        deletePoi(deleteTarget.id)
        setPois(loadPois())
        setDeleteTarget(null)
    }

    const handleEdit = (poi: POI) => {
        navigate({
            to: '/plan/poi',
            state: { editingPoi: poi } as Record<string, unknown>,
        })
    }

    return (
        <AutomaticSheet>
            <SheetHeader heading={t('Favourites')} share={false} />
            {pois.length === 0
                ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {t('No saved places yet')}
                            </Typography>
                        </Box>
                    )
                : (
                        <List>
                            {pois.map(poi => (
                                <ListItem
                                    key={poi.id}
                                    secondaryAction={(
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton edge="end" onClick={() => handleEdit(poi)}>
                                                <EditIcon sx={{ fontSize: 20 }} />
                                            </IconButton>
                                            <IconButton edge="end" onClick={() => setDeleteTarget(poi)}>
                                                <DeleteIcon sx={{ fontSize: 20 }} />
                                            </IconButton>
                                        </Stack>
                                    )}
                                    sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                                >
                                    <Stack direction="row" alignItems="center" gap={1.5}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {ICON_MAP[poi.icon] ?? <StarIcon sx={{ color: 'primary.main' }} />}
                                        </Box>
                                        <Box>
                                            <Typography variant="body1">{poi.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {poi.lat.toFixed(4)}
                                                ,
                                                {poi.lon.toFixed(4)}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </ListItem>
                            ))}
                        </List>
                    )}

            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>{t('Delete location?')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('This will permanently remove "{{name}}".', { name: deleteTarget?.name ?? '' })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>{t('Cancel')}</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">{t('Delete')}</Button>
                </DialogActions>
            </Dialog>
        </AutomaticSheet>
    )
}
